import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import CreateFileModal from "../modals/CreateFile";
import DeleteFileModal from "../modals/DeleteFile";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  IconButton,
  Box,
  TableHead,
  TableBody,
  Paper,
  TableContainer,
  Table,
  TableCell,
  TableRow,
} from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { StyledTableCell, StyledTableRow } from "../../styles/table";
import { getHeaders } from "../../utils/common";
import Loading from "../../Loading";
import ErrorDisplay from "../../components/error/ErrorDisplay";
import { TableWrapper } from "../../components/general/TableWrapper";

let cachedList: file[] = [];
let cachedListTarget = "";
let cachedReadOnly = true;

/**
 * @type {file}
 * @param {string} id id of the file
 * @param {string} name name od the file
 * @param {string} type type of file - yaml,config & generic
 */
export type file = {
  id: string;
  name: string;
  type: string;
};

/**
 * @typedef {Interface} FileListParams
 * @prop {boolean} hiddenSmall
 * @prop {String} sessionID
 * @prop {String} selectedFile
 * @prop {String} defaultConfigType
 * @prop {boolean} [alwaysShow]
 */
interface FileListParams {
  sessionID: string;
  selectedFile: string;
  defaultConfigType: string; // TODO: Enum
}

function linkType(defaultType: string, path: string) {
  let tmpLinkType = "generic";
  if (path.endsWith("yaml") || path.endsWith("yml")) {
    tmpLinkType = defaultType;
  } else if (path.endsWith("blobl")) {
    tmpLinkType = "blobl";
  }
  return tmpLinkType;
}

/**
 * file list
 * @param {FileListParams}
 * @returns {JSX.Element}
 */
export default function FileList({
  sessionID,
  selectedFile,
  defaultConfigType,
}: FileListParams) {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(sessionID !== cachedListTarget);
  const [fetchError, setFetchError] = useState(null);
  const [readOnly, setReadOnly] = useState(cachedReadOnly);
  const [files, setFiles] = useState(cachedList);
  const [fileId, setFileId] = useState("");

  const [createFileModalIsOpen, setCreateFileModalIsOpen] = useState(false);
  const [deleteFileModalTarget, setDeleteFileModalTarget] = useState("");
  const [deleteFileModalIsOpen, setDeleteFileModalIsOpen] = useState(false);

  defaultConfigType = defaultConfigType || "graph";

  const myHeaders = getHeaders();
  myHeaders.append("Content-Type", "application/json");

  useEffect(() => {
    if (cachedListTarget !== sessionID) {
      cachedList = [];
      cachedReadOnly = true;
    }
    if (cachedList.length > 0) {
      setFiles(cachedList);
      setReadOnly(cachedReadOnly);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFiles([]);
  }, [sessionID]);

  const refreshList = useCallback(() => {
    cachedList = [];
    cachedReadOnly = true;
    cachedListTarget = "";
    setLoading(true);
    setFiles([]);
  }, []);

  const onDelete = useCallback(
    (path: any) => {
      refreshList();
      if (path === selectedFile) {
        navigate(`/app/view/s/${sessionID}`);
      }
    },
    [sessionID, selectedFile, refreshList]
  );

  const onCreate = useCallback(
    (path: any, fileType: any) => {
      if (path === undefined) {
        refreshList();
        return;
      }
      refreshList();
      if (fileType !== "folder") {
        const tmpLinkType = linkType(defaultConfigType, path);
        navigate(
          `/app/view/s/${sessionID}/${encodeURIComponent(path)}/${tmpLinkType}`
        );
      }
    },
    [sessionID, defaultConfigType, refreshList]
  );

  const fetchURL = `/mqttapi/v1/session/${sessionID}/files`;

  useEffect(() => {
    if (isLoading) {
      fetch(fetchURL, { headers: myHeaders, method: "GET" })
        .then(async (response) => {
          if (response.ok) {
            setFetchError(null);
            return response.json();
          }
          const text = await response.text();
          throw new Error(`${response.statusText}: ${text}`);
        })
        .then((json) => {
          cachedList = json.files;
          cachedReadOnly = json.request_role === "reader";
          setFiles(cachedList);
          setReadOnly(cachedReadOnly);
          cachedListTarget = sessionID;
        })
        .catch((error) => {
          setFetchError(error);
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchURL, setFetchError, sessionID, isLoading]);

  let fileAddBtn = null;
  if (!readOnly) {
    fileAddBtn = (
      <IconButton
        title="create a file"
        onClick={() => {
          setCreateFileModalIsOpen(true);
        }}
      >
        <AddBoxOutlinedIcon />
      </IconButton>
    );
  }

  return (
    <Box
      sx={{
        width: "100%", // Ensures it takes the full width of the parent
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 1,
          alignItems: "center",
        }}
      >
        <Box>
          <strong>Files</strong>
        </Box>
        <Box>
          {fileAddBtn}
          <IconButton onClick={refreshList}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
      {isLoading ? (
        <Loading />
      ) : fetchError !== null ? (
        <ErrorDisplay err={fetchError} url={fetchURL} />
      ) : (
        <>
          {(files || []).length === 0 ? (
            <>
              <button
                className={"firstSessionBtn"}
                onClick={() => {
                  setCreateFileModalIsOpen(true);
                }}
              >
                Add your first file
              </button>
              <div className={"firstSessionBlurb"}>
                <div className={"firstSessionHand"}>☝️</div>
                Click here to create your first config in the session
              </div>
            </>
          ) : null}
          {
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="projects table">
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {files.map((file, idx) => {
                    let fileLinkType = "generic";
                    if (file.type === "config") {
                      fileLinkType = defaultConfigType;
                    } else if (file.type === "bloblang") {
                      fileLinkType = "blobl";
                    }
                    return (
                      <>
                        <TableRow
                          key={idx}
                          sx={{
                            "&:last-child td, &:last-child th": {
                              border: 0,
                            },
                          }}
                        >
                          <TableCell>
                            <Link
                              style={{ textDecoration: "none" }}
                              to={`/app/view/s/${sessionID}/${encodeURIComponent(
                                file.name
                              )}/${fileLinkType}`}
                            >
                              {file.name}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              title="delete file"
                              onClick={() => {
                                setDeleteFileModalTarget(file.name);
                                setDeleteFileModalIsOpen(true);
                                setFileId(file.id);
                              }}
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })}
                  {/* </List> */}
                </TableBody>
              </Table>
            </TableContainer>
          }
        </>
      )}

      {/* modalss */}
      <CreateFileModal
        sessionID={sessionID}
        isOpen={createFileModalIsOpen}
        subDir={""}
        closeModal={() => setCreateFileModalIsOpen(false)}
        onCreate={onCreate}
        files={files}
      />
      <DeleteFileModal
        sessionID={sessionID}
        isOpen={deleteFileModalIsOpen}
        target={deleteFileModalTarget}
        closeModal={() => setDeleteFileModalIsOpen(false)}
        onDelete={onDelete}
        fileId={fileId}
      />
    </Box>
  );
}
